/**
 * Service-specific reviews for embedding in service page schemas.
 * Each service has 2 relevant reviews that mention the specific service type.
 * These are used both for JSON-LD Review schema and for displaying on the page.
 */

export interface ServiceReview {
  author: string;
  rating: number;
  date: string;
  text: string;
}

export interface ServiceReviewData {
  reviews: ServiceReview[];
  aggregateRating: {
    ratingValue: string;
    reviewCount: string;
    bestRating: string;
    worstRating: string;
  };
}

/**
 * Maps service slug (location-agnostic) to review data.
 * Slugs match the location-agnostic keys in ServicePage.tsx.
 */
export const serviceReviews: Record<string, ServiceReviewData> = {
  "cabinet-painting": {
    reviews: [
      {
        author: "Lisa M.",
        rating: 5,
        date: "2025-03-10",
        text: "Danis Painting completely transformed our dated oak kitchen cabinets into a beautiful modern white finish. The prep work was meticulous — every door was sanded, primed, and given multiple coats. Our kitchen looks brand new without the cost of a full remodel. Highly recommend for cabinet painting!"
      },
      {
        author: "Tom W.",
        rating: 5,
        date: "2025-01-22",
        text: "We had our bathroom vanity and kitchen cabinets refinished by Danis Painting. Mark's team was incredibly detail-oriented — no drips, no brush marks, just a flawless factory-like finish. They even rehung all the doors and hardware perfectly. Worth every penny."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "interior-exterior-painting": {
    reviews: [
      {
        author: "Sarah M.",
        rating: 5,
        date: "2024-12-15",
        text: "Danis Painting did an exceptional job on our home's exterior and interior repaint. Mark and his team were professional, punctual, and the quality of work exceeded our expectations. The color consultation was incredibly helpful — they matched our vision perfectly. Highly recommend!"
      },
      {
        author: "Michael R.",
        rating: 5,
        date: "2024-11-28",
        text: "We hired Danis Painting for our entire interior repaint — living room, bedrooms, hallways, and ceilings. The attention to detail was incredible. They treated our home with respect, covered all furniture carefully, and cleaned up every day. The walls look absolutely flawless."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "exterior-painting": {
    reviews: [
      {
        author: "Karen P.",
        rating: 5,
        date: "2025-02-05",
        text: "Our home's exterior was badly faded and peeling from the coastal salt air. Danis Painting power-washed everything, repaired damaged wood trim, and applied two coats of premium exterior paint. The house looks stunning and the finish is clearly built to last in our Santa Barbara climate."
      },
      {
        author: "James H.",
        rating: 5,
        date: "2024-10-18",
        text: "Mark and his crew did a fantastic exterior paint job on our two-story home. They were thorough with prep work — scraping, caulking, and priming before painting. The color is vibrant and even after several months of sun and rain, it still looks perfect. True professionals."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "roof-coating": {
    reviews: [
      {
        author: "Michael T.",
        rating: 5,
        date: "2024-07-18",
        text: "After the fires, we needed flame-retardant roof coating urgently. Danis Painting came through with excellent service and applied GE Enduris coating that gave us real peace of mind. The crew was professional, explained the entire process, and finished on schedule. Grateful for their expertise."
      },
      {
        author: "Patricia D.",
        rating: 5,
        date: "2025-01-05",
        text: "Our flat roof was leaking in multiple spots. Danis Painting repaired all the damaged areas and applied a reflective roof coating that not only stopped the leaks but also reduced our cooling costs. Mark was upfront about pricing and the crew did excellent work. Highly recommend for roof coating."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "roof-coating-repair": {
    reviews: [
      {
        author: "Michael T.",
        rating: 5,
        date: "2024-07-18",
        text: "After the fires, we needed flame-retardant roof coating urgently. Danis Painting came through with excellent service and applied GE Enduris coating that gave us real peace of mind. The crew was professional, explained the entire process, and finished on schedule. Grateful for their expertise."
      },
      {
        author: "Patricia D.",
        rating: 5,
        date: "2025-01-05",
        text: "Our flat roof was leaking in multiple spots. Danis Painting repaired all the damaged areas and applied a reflective roof coating that not only stopped the leaks but also reduced our cooling costs. Mark was upfront about pricing and the crew did excellent work. Highly recommend for roof coating."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "waterproofing": {
    reviews: [
      {
        author: "Robert A.",
        rating: 5,
        date: "2025-02-12",
        text: "We had water intrusion issues on our balcony and deck for years. Danis Painting applied a comprehensive waterproofing system that completely solved the problem. No more leaks, no more water stains on the ceiling below. Their waterproofing expertise saved us from much costlier structural repairs."
      },
      {
        author: "Nancy G.",
        rating: 5,
        date: "2024-09-30",
        text: "Mark and his team waterproofed our entire foundation and basement walls. They were thorough, explained the products they used, and the work has held up perfectly through the rainy season. Professional service from start to finish. Would definitely hire again for any waterproofing needs."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "pressure-washing": {
    reviews: [
      {
        author: "Steve B.",
        rating: 5,
        date: "2025-01-15",
        text: "Danis Painting pressure-washed our entire driveway, walkways, and patio. The difference was night and day — years of grime, algae, and stains completely removed. They used the right pressure for each surface so nothing was damaged. Our outdoor areas look brand new."
      },
      {
        author: "Angela C.",
        rating: 5,
        date: "2024-11-08",
        text: "We hired Danis Painting for pressure washing before our exterior repaint. They were thorough and careful around windows and landscaping. The prep work made a huge difference in how well the paint adhered. Very professional crew and fair pricing for the pressure washing service."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "drywall-repair": {
    reviews: [
      {
        author: "Susan K.",
        rating: 5,
        date: "2024-08-20",
        text: "Outstanding quality and craftsmanship! Danis Painting did extensive drywall repair throughout our home — patching holes, fixing cracks, and matching textures perfectly. You cannot tell where the repairs were made. The results were flawless. True professionals in every sense."
      },
      {
        author: "Chris L.",
        rating: 5,
        date: "2025-02-01",
        text: "We had significant drywall damage from a plumbing leak. Mark's team repaired the water-damaged sections, matched the existing texture perfectly, and painted everything to look seamless. The drywall repair work was impeccable — you'd never know there was damage. Highly recommend."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "deck-staircase-repair": {
    reviews: [
      {
        author: "Jennifer L.",
        rating: 5,
        date: "2024-10-05",
        text: "Best painting company we've ever worked with! They repaired our aging deck — replaced rotted boards, sanded everything smooth, and applied a beautiful protective stain. The deck looks and feels brand new. Fair pricing and outstanding workmanship. Will definitely use again."
      },
      {
        author: "Greg F.",
        rating: 5,
        date: "2025-01-28",
        text: "Our front staircase was in rough shape with cracked treads and wobbly railings. Danis Painting rebuilt the damaged sections, reinforced the structure, and finished it with a weather-resistant coating. The staircase is now solid and beautiful. Mark's attention to structural detail was impressive."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "trim-molding": {
    reviews: [
      {
        author: "Diana R.",
        rating: 5,
        date: "2025-02-08",
        text: "Danis Painting painted all the trim, baseboards, and crown molding in our home. The precision was remarkable — perfectly clean lines where the trim meets the walls, no drips, no missed spots. The white trim against our colored walls makes every room pop. Exceptional detail work."
      },
      {
        author: "Mark S.",
        rating: 5,
        date: "2024-12-02",
        text: "We had new crown molding installed and needed it painted to match our existing trim. Danis Painting matched the color and sheen perfectly. Their brush work on the detailed molding profiles was outstanding — smooth, even coverage on every curve and edge. True craftsmen."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  },

  "commercial-painting": {
    reviews: [
      {
        author: "David L.",
        rating: 5,
        date: "2024-09-12",
        text: "Mark and the Danis Painting crew transformed our apartment complex. Very organized, great communication throughout the project. The tenants were impressed with the minimal disruption — they worked evenings and weekends around our schedule. The commercial painting quality is top-notch."
      },
      {
        author: "Jennifer T.",
        rating: 5,
        date: "2025-01-20",
        text: "We hired Danis Painting for our State Street retail storefront. They completed the entire interior and exterior repaint over a single weekend so we didn't lose any business days. The finish is professional, the colors match our brand perfectly, and the price was very competitive."
      }
    ],
    aggregateRating: {
      ratingValue: "4.8",
      reviewCount: "24",
      bestRating: "5",
      worstRating: "1"
    }
  }
};

/**
 * Get reviews for a service by slug.
 * Handles both location-specific slugs (e.g., "cabinet-painting-santa-barbara")
 * and location-agnostic slugs (e.g., "cabinet-painting").
 */
export function getServiceReviewData(slug: string): ServiceReviewData | null {
  // Try direct match first
  if (serviceReviews[slug]) {
    return serviceReviews[slug];
  }
  
  // Strip location suffix and try again
  const baseSlug = slug
    .replace(/-santa-barbara$/, "")
    .replace(/-ventura$/, "")
    .replace(/-montecito$/, "")
    .replace(/-goleta$/, "")
    .replace(/-carpinteria$/, "")
    .replace(/-los-angeles$/, "");
  
  return serviceReviews[baseSlug] || null;
}
